import React, { useState, useEffect } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithCustomToken, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, onSnapshot, query, where, getDocs, addDoc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { BookOpen, Gamepad2, Trophy, Check, Eye, Wine, CheckCircle, Upload, LogOut, LogIn, UserPlus, X, Users, ShieldCheck, Plus, Edit3, Trash2, Globe, User, Search, ChevronRight, Link, UserMinus, ChevronUp, ChevronDown, MessageSquare } from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyACX16MzMdRxishXzXcTiGCakBEPXKjx_g",
  authDomain: "vine-master.firebaseapp.com",
  projectId: "vine-master",
  storageBucket: "vine-master.firebasestorage.app",
  messagingSenderId: "746408540597",
  appId: "1:746408540597:web:2b4a1609346416cd4f41fc",
  measurementId: "G-W46J6DXD0P"
};

const appId = 'vine-master-app';
const initialAuthToken = null;

// --- Styling ---
const theme = {
    primary: '#722F37',
    primaryHover: '#5a252b',
    secondary: '#D4AF37',
    bg: '#Fdfbf7',
    surface: '#ffffff',
    text: '#2d3748',
    textMuted: '#718096',
    border: '#e2e8f0',
    error: '#e53e3e',
    success: '#38a169'
};

const styles = {
    appBackground: { 
        minHeight: '100vh', 
        width: '100%',
        backgroundImage: 'linear-gradient(rgba(30, 15, 15, 0.6), rgba(30, 15, 15, 0.8)), url("https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?auto=format&fit=crop&w=2074&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: 'clamp(1rem, 4vw, 3rem)',
        boxSizing: 'border-box'
    },
    appCanvas: {
        backgroundColor: theme.bg,
        width: '100%',
        maxWidth: '1200px',
        borderRadius: '1.25rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif",
        color: theme.text,
        minHeight: '85vh'
    },
    header: { backgroundColor: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: '1.25rem clamp(1rem, 3vw, 2rem)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logo: { display: 'flex', alignItems: 'center', gap: '0.75rem', color: theme.primary, fontWeight: 800, fontSize: 'clamp(1.25rem, 2vw, 1.5rem)', letterSpacing: '-0.025em' },
    nav: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
    navBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', background: 'none', color: theme.textMuted, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' },
    main: { flexGrow: 1, padding: 'clamp(1rem, 3vw, 2rem)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' },
    card: { backgroundColor: theme.surface, borderRadius: '1rem', padding: 'clamp(1.5rem, 4vw, 2.5rem)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: `1px solid ${theme.border}`, width: '100%', minHeight: '500px', position: 'relative' },
    input: { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: `1px solid ${theme.border}`, marginBottom: '1rem', fontSize: '1rem', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: `1px solid ${theme.border}`, marginBottom: '1rem', fontSize: '1rem', boxSizing: 'border-box', minHeight: '100px', fontFamily: 'inherit' },
    btnPrimary: { backgroundColor: theme.primary, color: 'white', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', border: 'none', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'background 0.2s' },
    btnSecondary: { backgroundColor: theme.surface, color: theme.primary, border: `1px solid ${theme.primary}`, padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 60, padding: '1rem', backdropFilter: 'blur(4px)' },
    modalContent: { backgroundColor: theme.surface, padding: '2rem', borderRadius: '1.25rem', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }
};

// --- CUSTOM VANILLA CSS INJECTIONS ---
const globalCSS = `
    .nav-btn:hover { background-color: #f7fafc; color: ${theme.primary}; }
    .nav-btn.active { color: ${theme.primary}; background-color: #fff5f5; }
    .btn-primary:hover { background-color: ${theme.primaryHover}; }
    .tab-btn { padding: 0.75rem 1.5rem; border: none; background: none; font-weight: 600; color: ${theme.textMuted}; cursor: pointer; border-bottom: 2px solid transparent; }
    .tab-btn.active { color: ${theme.primary}; border-bottom-color: ${theme.primary}; }
    
    .controls-row { display: flex; flex-direction: column; align-items: center; gap: 1.25rem; background-color: #ffffff; padding: 1.25rem; border-radius: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; margin-bottom: 1.5rem; }
    .segmented-track { display: flex; background-color: rgba(226, 232, 240, 0.6); padding: 0.375rem; border-radius: 1rem; box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); gap: 0.25rem; width: 100%; max-width: 450px; }
    .segment-btn { flex: 1; display: flex; justify-content: center; align-items: center; gap: 0.5rem; padding: 0.6rem 1rem; border-radius: 0.75rem; border: none; background: transparent; color: #718096; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; cursor: pointer; transition: all 0.3s ease; }
    .segment-btn:hover:not(.active) { background-color: rgba(226, 232, 240, 0.5); color: #4a5568; }
    .segment-btn.active { background: linear-gradient(to bottom, #ffffff, #f8fafc); color: #722F37; box-shadow: 0 2px 4px rgba(0,0,0,0.05); transform: scale(1.02); border: 1px solid rgba(226, 232, 240, 0.8); }
    
    .search-add-row { display: flex; width: 100%; max-width: 800px; gap: 0.75rem; }
    .btn-gradient { display: flex; align-items: center; gap: 0.5rem; padding: 0.625rem 1.5rem; background: linear-gradient(to bottom, #9b2c2c, #722F37); color: white; border: none; border-radius: 0.75rem; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; cursor: pointer; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); transition: all 0.2s ease; white-space: nowrap; }
    .btn-gradient:hover { background: linear-gradient(to bottom, #822727, #5a252b); box-shadow: 0 6px 8px -1px rgba(0,0,0,0.15); transform: translateY(-1px); }
    
    .search-container { position: relative; flex-grow: 1; }
    .search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #a0aec0; }
    .search-input { width: 100%; padding: 0.75rem 1rem 0.75rem 2.75rem; background-color: #f7fafc; border: 1px solid #e2e8f0; border-radius: 0.75rem; font-size: 0.875rem; box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.03); box-sizing: border-box; transition: all 0.2s; }
    .search-input:focus { outline: none; background-color: #ffffff; border-color: #722F37; box-shadow: 0 0 0 3px rgba(114, 47, 55, 0.1); }
    
    .pill-varietal { background-color: #fffbeb; color: #92400e; border: 1px solid #fde68a; border-radius: 0.375rem; padding: 0.25rem 0.6rem; font-size: 0.7rem; font-weight: 600; display: inline-block; margin: 0.15rem; white-space: nowrap; }
    .pill-region { background-color: #f8fafc; color: #4a5568; border: 1px solid #e2e8f0; border-radius: 0.375rem; padding: 0.25rem 0.6rem; font-size: 0.7rem; font-weight: 500; display: inline-block; margin: 0.15rem; white-space: nowrap; }
    
    .table-container { overflow-x: auto; background-color: white; border-radius: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; }
    .styled-table { width: 100%; text-align: left; border-collapse: collapse; font-size: 0.875rem; }
    .styled-table th { padding: 1rem; background-color: #f8fafc; color: #2d3748; font-weight: 700; border-bottom: 1px solid #e2e8f0; cursor: pointer; transition: background 0.2s; white-space: nowrap; }
    .styled-table th:hover { background-color: #edf2f7; }
    .styled-table td { padding: 1rem; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
    .table-row-hover { cursor: pointer; transition: background-color 0.2s; }
    .table-row-hover:hover { background-color: #fffdf5; }
    .chevron-icon { color: #cbd5e0; transition: color 0.2s; }
    .table-row-hover:hover .chevron-icon { color: #722F37; }
    
    .flex-center { display: flex; justify-content: center; align-items: center; }
    .flex-wrap-gap { display: flex; flex-wrap: wrap; gap: 0.25rem; }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = globalCSS;
document.head.appendChild(styleSheet);


export default function App() {
    const [view, setView] = useState('welcome');
    const [user, setUser] = useState({ 
        id: null, name: '', email: '', orgId: null, 
        roles: { isUser: false, isModerator: false, isFacilitator: false, isAdmin: false },
        isInitializing: true 
    });
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [storage, setStorage] = useState(null);

    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joinPasscode, setJoinPasscode] = useState('');
    const [joinError, setJoinError] = useState('');
    const [joining, setJoining] = useState(false);
    const [joinMode, setJoinMode] = useState('join'); // 'join' or 'request'
    const [requestData, setRequestData] = useState({ name: '', expectedUsers: '' });
    const [requestStatus, setRequestStatus] = useState('');
    const [userOrgName, setUserOrgName] = useState('');

// Fetch the readable organization name when a user logs in
useEffect(() => {
    if (user.orgId && db) {
        const fetchOrgName = async () => {
            try {
                const orgSnap = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'orgs', user.orgId));
                if (orgSnap.exists()) {
                    setUserOrgName(orgSnap.data().name);
                }
            } catch (err) {
                console.error("Error fetching org name:", err);
            }
        };
        fetchOrgName();
    } else {
        setUserOrgName('');
    }
}, [user.orgId, db]);

    useEffect(() => {
        const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
        const authInstance = getAuth(app);
        const firestore = getFirestore(app);
        const storageInstance = getStorage(app);
        
        setAuth(authInstance);
        setDb(firestore);
        setStorage(storageInstance);

        let profileUnsub;

        const unsubscribe = onAuthStateChanged(authInstance, (firebaseUser) => {
            if (profileUnsub) {
                profileUnsub();
                profileUnsub = null;
            }

            if (firebaseUser) {
                const userRef = doc(firestore, 'artifacts', appId, 'public', 'data', 'users', firebaseUser?.uid);
                
                profileUnsub = onSnapshot(userRef, (userSnap) => {
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        setUser({
                            id: firebaseUser.uid,
                            name: userData.name,
                            email: firebaseUser.email,
                            orgId: userData.orgId,
                            roles: userData.roles || { isUser: true, isModerator: false, isFacilitator: false, isAdmin: false },
                            isInitializing: false
                        });
                        setView('study'); 
                    } else {
                        setUser(prev => ({...prev, isInitializing: false}));
                    }
                }, (error) => {
                    console.error("Profile fetch error:", error);
                    setUser(prev => ({...prev, isInitializing: false}));
                });
            } else {
                if (initialAuthToken) {
                    signInWithCustomToken(authInstance, initialAuthToken).catch(console.error);
                } else {
                    setUser({ id: null, isInitializing: false });
                }
            }
        });
        
        return () => {
            unsubscribe();
            if (profileUnsub) profileUnsub();
        };
    }, []);

    const handleLogout = async () => {
        if (auth) {
            await signOut(auth);
            setView('welcome');
            setUser({ id: null, isInitializing: false });
        }
    };

    const handleJoinTeam = async (e) => {
        e.preventDefault();
        setJoinError('');
        setJoining(true);
        try {
            if (!joinPasscode) throw new Error("Passcode is required.");
            const orgsRef = collection(db, 'artifacts', appId, 'public', 'data', 'orgs');
            const q = query(orgsRef, where("passcode", "==", joinPasscode.trim()));
            const snapshot = await getDocs(q);
            if (snapshot.empty) throw new Error("Invalid Organization Passcode.");
            
            const validatedOrgId = snapshot.docs[0].id;
            
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', user.id), {
                orgId: validatedOrgId
            });
            
            setShowJoinModal(false);
            setJoinPasscode('');
        } catch (err) {
            setJoinError(err.message);
        } finally {
            setJoining(false);
        }
    };
    
    const handleRequestTeam = async (e) => {
        e.preventDefault();
        setRequestStatus('Submitting...');
        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'team_requests'), {
                restaurantName: requestData.name,
                expectedUsers: requestData.expectedUsers,
                requestedBy: user.id,
                requesterName: user.name,
                requesterEmail: user.email,
                status: 'pending',
                createdAt: serverTimestamp()
            });
            setRequestStatus('Success! The admin team will review your request.');
            setTimeout(() => {
                setShowJoinModal(false);
                setJoinMode('join');
                setRequestStatus('');
                setRequestData({ name: '', expectedUsers: '' });
            }, 2500);
        } catch(err) {
            setRequestStatus('Error: ' + err.message);
        }
    };

    if (user.isInitializing) return (
        <div style={{...styles.appBackground, alignItems: 'center'}}>
            <div style={{...styles.appCanvas, minHeight: 'auto', padding: '4rem', alignItems: 'center', justifyContent: 'center'}}>
                Loading Vine Master...
            </div>
        </div>
    );

    return (
        <div style={styles.appBackground}>
            <div style={styles.appCanvas}>
                <header style={styles.header}>
                    <div style={styles.logo}>
                        <Wine size={28} />
                        <span>Vine Master</span>
                    </div>
                    {user.id && (
                        <nav style={styles.nav}>
                            <button className={`nav-btn ${view === 'study' ? 'active' : ''}`} style={styles.navBtn} onClick={() => setView('study')}><BookOpen size={18} /> Study</button>
                            <button className={`nav-btn ${view === 'play' ? 'active' : ''}`} style={styles.navBtn} onClick={() => setView('play')}><Gamepad2 size={18} /> Play</button>
                            <button className={`nav-btn ${view === 'scores' ? 'active' : ''}`} style={styles.navBtn} onClick={() => setView('scores')}><Trophy size={18} /> Scores</button>
                            
                            {user.roles.isModerator && (
                                <button className={`nav-btn ${view === 'moderate' ? 'active' : ''}`} style={styles.navBtn} onClick={() => setView('moderate')}><CheckCircle size={18} /> Approvals</button>
                            )}
                            {user.roles.isFacilitator && (
                                <button className={`nav-btn ${view === 'facilitator' ? 'active' : ''}`} style={styles.navBtn} onClick={() => setView('facilitator')}><Upload size={18} /> Facilitator</button>
                            )}
                            
                            {/* NEW: Display Team Badge OR Join Button */}
                        {user.orgId ? (
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem', padding: '0.4rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '2rem', border: '1px solid #bbf7d0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}}>
                                <Users size={16} color="#047857" />
                                <span style={{fontSize: '0.875rem', fontWeight: 700, color: '#047857'}}>{userOrgName || 'Loading Team...'}</span>
                            </div>
                        ) : (
                            <button className="nav-btn" style={{...styles.navBtn, color: theme.secondary}} onClick={() => setShowJoinModal(true)}>
                                <Link size={18} /> Join / Start Team
                            </button>
                        )}

                            <button className="nav-btn" style={{...styles.navBtn, marginLeft: '1rem', color: theme.error}} onClick={handleLogout}><LogOut size={18} /> Logout</button>
                        </nav>
                    )}
                </header>

                <main style={styles.main}>
                    <div style={styles.card}>
                        {!user.id ? (
                            <WelcomeView auth={auth} db={db} setView={setView} />
                        ) : (
                            <>
                                {view === 'study' && <StudyView db={db} storage={storage} user={user} />}
                                {view === 'play' && <PlayView db={db} user={user} />}
                                {view === 'scores' && <ScoresView db={db} user={user} />}
                                {view === 'moderate' && <ModeratorView db={db} user={user} />}
                                {view === 'facilitator' && <FacilitatorView db={db} user={user} />}
                                {view === 'admin' && <AdminView db={db} user={user} />}
                            </>
                        )}
                    </div>
                </main>
                
                <footer style={{textAlign: 'center', padding: '1.5rem', fontSize: '0.75rem', backgroundColor: theme.surface, borderTop: `1px solid ${theme.border}`}}>
                    <button onClick={() => setView('admin')} style={{background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', opacity: 0.5}}>Admin Access</button>
                </footer>
            </div>

{showJoinModal && (
                <div style={styles.modalOverlay}>
                    <div style={{...styles.modalContent, maxWidth: '400px', padding: '2rem'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                            <h2 style={{fontSize: '1.5rem', color: theme.primary}}>Team Access</h2>
                            <button onClick={() => {setShowJoinModal(false); setJoinError(''); setRequestStatus('');}} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#718096'}}><X size={24}/></button>
                        </div>
                        
                        <div style={{display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', backgroundColor: '#f7fafc', padding: '0.25rem', borderRadius: '0.5rem'}}>
                            <button onClick={() => setJoinMode('join')} style={{flex: 1, padding: '0.5rem', border: 'none', borderRadius: '0.25rem', fontWeight: 600, cursor: 'pointer', backgroundColor: joinMode === 'join' ? 'white' : 'transparent', color: joinMode === 'join' ? theme.primary : '#718096', boxShadow: joinMode === 'join' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}}>Join Team</button>
                            <button onClick={() => setJoinMode('request')} style={{flex: 1, padding: '0.5rem', border: 'none', borderRadius: '0.25rem', fontWeight: 600, cursor: 'pointer', backgroundColor: joinMode === 'request' ? 'white' : 'transparent', color: joinMode === 'request' ? theme.primary : '#718096', boxShadow: joinMode === 'request' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}}>Start a Team</button>
                        </div>

                        {joinMode === 'join' ? (
                            <form onSubmit={handleJoinTeam}>
                                <p style={{color: theme.textMuted, fontSize: '0.875rem', marginBottom: '1.5rem'}}>Enter your organization's passcode to connect your account to your team.</p>
                                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.5rem'}}>Organization Passcode</label>
                                <input type="text" value={joinPasscode} onChange={e => setJoinPasscode(e.target.value)} style={styles.input} placeholder="e.g. BRAMBLE-2024" required />
                                {joinError && <p style={{color: theme.error, fontSize: '0.875rem', marginBottom: '1rem'}}>{joinError}</p>}
                                <button type="submit" style={{...styles.btnPrimary, width: '100%', padding: '0.875rem'}} disabled={joining}>
                                    {joining ? 'Verifying...' : 'Join Organization'}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleRequestTeam}>
                                <p style={{color: theme.textMuted, fontSize: '0.875rem', marginBottom: '1.5rem'}}>Bring Vine Master to your restaurant. Submitting this request will automatically upgrade your account to Team Facilitator upon approval.</p>
                                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.5rem'}}>Restaurant / Team Name</label>
                                <input type="text" value={requestData.name} onChange={e => setRequestData({...requestData, name: e.target.value})} style={styles.input} placeholder="e.g. Credence" required />
                                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.5rem'}}>Expected Number of Users</label>
                                <input type="number" value={requestData.expectedUsers} onChange={e => setRequestData({...requestData, expectedUsers: e.target.value})} style={styles.input} placeholder="e.g. 15" required />
                                
                                {requestStatus && <p style={{color: requestStatus.includes('Error') ? theme.error : theme.success, fontSize: '0.875rem', marginBottom: '1rem', fontWeight: 600}}>{requestStatus}</p>}
                                
                                <button type="submit" style={{...styles.btnPrimary, width: '100%', padding: '0.875rem'}} disabled={!!requestStatus}>
                                    Submit Request
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// WELCOME & AUTH
// -----------------------------------------------------------------------------
function WelcomeView({ auth, db }) {
    const [mode, setMode] = useState('login'); 
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [orgCode, setOrgCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAuth = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (mode === 'register') {
                if (!name || !email || !password) throw new Error("Name, email, and password are required.");
                
                let validatedOrgId = null;
                if (orgCode) {
                    const orgsRef = collection(db, 'artifacts', appId, 'public', 'data', 'orgs');
                    const q = query(orgsRef, where("passcode", "==", orgCode.trim()));
                    const snapshot = await getDocs(q);
                    if (snapshot.empty) throw new Error("Invalid Organization Passcode.");
                    validatedOrgId = snapshot.docs[0].id;
                }

                const cred = await createUserWithEmailAndPassword(auth, email, password);
                
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', cred.user?.uid), {
                    name,
                    email,
                    orgId: validatedOrgId,
                    roles: { isUser: true, isModerator: false, isFacilitator: false, isAdmin: false },
                    createdAt: serverTimestamp()
                });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{maxWidth: '400px', margin: '2rem auto', textAlign: 'center'}}>
            <h2 style={{fontSize: '2rem', color: theme.primary, marginBottom: '0.5rem'}}>
                {mode === 'login' ? 'Welcome Back' : 'Create an Account'}
            </h2>
            <p style={{color: theme.textMuted, marginBottom: '2rem'}}>
                {mode === 'login' ? 'Sign in to continue your wine education.' : 'Join Vine Master to start crowdsourcing tasting notes.'}
            </p>

            <form onSubmit={handleAuth}>
                {mode === 'register' && (
                    <input type="text" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} style={styles.input} />
                )}
                <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} style={styles.input} />
                <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} style={styles.input} />
                
                {mode === 'register' && (
                    <div style={{marginTop: '1rem', borderTop: `1px solid ${theme.border}`, paddingTop: '1rem'}}>
                        <label style={{display: 'block', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem'}}>Organization Passcode (Optional)</label>
                        <input type="text" placeholder="e.g. BRAMBLE-2024" value={orgCode} onChange={e => setOrgCode(e.target.value)} style={styles.input} />
                        <p style={{fontSize: '0.75rem', color: theme.textMuted, textAlign: 'left'}}>Enter a code provided by your manager, or leave blank for a personal account.</p>
                    </div>
                )}

                {error && <p style={{color: theme.error, fontSize: '0.875rem', marginBottom: '1rem'}}>{error}</p>}

                <button type="submit" style={{...styles.btnPrimary, width: '100%'}} disabled={loading}>
                    {loading ? 'Processing...' : (mode === 'login' ? <><LogIn size={18}/> Log In</> : <><UserPlus size={18}/> Sign Up</>)}
                </button>
            </form>

            <div style={{marginTop: '1.5rem'}}>
                <button onClick={() => {setMode(mode === 'login' ? 'register' : 'login'); setError('');}} style={{background: 'none', border: 'none', color: theme.primary, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline'}}>
                    {mode === 'login' ? "Don't have an account? Sign up" : "Already have an account? Log in"}
                </button>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// HELPER: SELECT OR ADD COMPONENT (Fixes Data Typos)
// -----------------------------------------------------------------------------
function SelectOrAdd({ label, value, options, onChange, placeholder, required }) {
    const isValueUnlisted = value !== '' && !options.includes(value);
    const [isAddingNew, setIsAddingNew] = useState(isValueUnlisted);

    if (isAddingNew) {
        return (
            <div>
                <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>{label} {required && '*'}</label>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                    <input 
                        type="text" 
                        required={required}
                        value={value} 
                        onChange={(e) => onChange(e.target.value)} 
                        style={{...styles.input, marginBottom: 0, padding: '0.5rem 0.75rem', fontSize: '0.875rem'}} 
                        placeholder={placeholder} 
                        autoFocus
                    />
                    <button 
                        type="button" 
                        onClick={() => { setIsAddingNew(false); onChange(''); }}
                        style={{...styles.btnSecondary, padding: '0.5rem 0.75rem', fontSize: '0.75rem', whiteSpace: 'nowrap'}}
                    >
                        List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>{label} {required && '*'}</label>
            <select 
                required={required}
                value={value} 
                onChange={(e) => {
                    if (e.target.value === '___ADD_NEW___') {
                        setIsAddingNew(true);
                        onChange('');
                    } else {
                        onChange(e.target.value);
                    }
                }}
                style={{...styles.input, marginBottom: 0, padding: '0.5rem 0.75rem', fontSize: '0.875rem', cursor: 'pointer', backgroundColor: '#f8fafc'}}
            >
                <option value="">Select...</option>
                {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                <option value="___ADD_NEW___" style={{fontWeight: 700, color: theme.primary}}>➕ Add New...</option>
            </select>
        </div>
    );
}

// -----------------------------------------------------------------------------
// STUDY VIEW & VINE EDITOR
// -----------------------------------------------------------------------------
function StudyView({ db, storage, user }) {
  const [vines, setVines] = useState([]);
  const [filter, setFilter] = useState('global');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVine, setSelectedVine] = useState(null); 
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingVineId, setEditingVineId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'producer', direction: 'asc' });
  const [formError, setFormError] = useState(''); // NEW: State for the mandatory note error

  // NEW: State for the community note input
  const [newNoteText, setNewNoteText] = useState('');

  const [formData, setFormData] = useState({
    producer: '', varietals: '', year: '', country: '', state: '', viticulturalArea: '', vineyard: '', story: '', suggestedPairings: ''
  });
  const [photoFile, setPhotoFile] = useState(null);

  const uniqueProducers = [...new Set(vines.map(v => v.producer).filter(Boolean))].sort();
  const uniqueVarietals = [...new Set(vines.map(v => v.varietals).filter(Boolean))].sort();
  const uniqueCountries = [...new Set(vines.map(v => v.country).filter(Boolean))].sort();
  const uniqueStates = [...new Set(vines.map(v => v.state).filter(Boolean))].sort();
  const uniqueAvas = [...new Set(vines.map(v => v.viticulturalArea).filter(Boolean))].sort();
  const uniqueVineyards = [...new Set(vines.map(v => v.vineyard).filter(Boolean))].sort();

  useEffect(() => {
    // FIX 1: Pointing directly to root 'vines_global' collection
    let q = collection(db, 'vines_global');
    if (filter === 'org' && user?.orgId) {
      q = query(collection(db, 'vines_global'), where('orgId', '==', user.orgId));
    } else if (filter === 'my') {
      q = query(collection(db, 'vines_global'), where('createdBy', '==', user?.id));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVines(docs);
      
      // Update selectedVine in real-time if it's currently open
      if (selectedVine) {
          const updatedSelected = docs.find(d => d.id === selectedVine.id);
          if (updatedSelected) setSelectedVine(updatedSelected);
      }
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, user, db]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredVines = vines.filter(vine => {
    const term = searchTerm.toLowerCase();
    return (
      (vine.producer && vine.producer.toLowerCase().includes(term)) ||
      (vine.varietals && vine.varietals.toLowerCase().includes(term)) ||
      (vine.country && vine.country.toLowerCase().includes(term)) ||
      (vine.state && vine.state.toLowerCase().includes(term)) ||
      (vine.viticulturalArea && vine.viticulturalArea.toLowerCase().includes(term)) ||
      (vine.vineyard && vine.vineyard.toLowerCase().includes(term)) ||
      (vine.suggestedPairings && vine.suggestedPairings.toLowerCase().includes(term))
    );
  }).sort((a, b) => {
    if ((a[sortConfig.key] || '') < (b[sortConfig.key] || '')) return sortConfig.direction === 'asc' ? -1 : 1;
    if ((a[sortConfig.key] || '') > (b[sortConfig.key] || '')) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // FIX 3: Make curator note (story) mandatory
    if (!formData.story.trim()) {
        setFormError("A curator note is required to establish the baseline flavor profile.");
        return;
    }

    setUploading(true);

    try {
      let photoUrl = ''; 
      if (photoFile) {
        const fileRef = ref(storage, `vine_photos/${user.id}_${Date.now()}_${photoFile.name}`);
        await uploadBytes(fileRef, photoFile);
        photoUrl = await getDownloadURL(fileRef);
      }

      const vinePayload = {
        producer: formData.producer.trim(),
        varietals: formData.varietals.trim(),
        year: formData.year.trim(),
        country: formData.country.trim(),
        state: formData.state.trim(),
        viticulturalArea: formData.viticulturalArea.trim(),
        vineyard: formData.vineyard.trim(),
        story: formData.story.trim(),
        suggestedPairings: formData.suggestedPairings.trim(),
        lastUpdatedBy: user?.id || 'unknown_user',
        updatedAt: new Date().toISOString()
      };

      if (photoUrl) vinePayload.photoUrl = photoUrl;

      if (editingVineId) {
        // FIX 1: Point to root collection
        await updateDoc(doc(db, 'vines_global', editingVineId), vinePayload);
      } else {
        vinePayload.createdBy = user?.id || 'unknown_user';
        vinePayload.creatorName = user?.name || 'Anonymous';
        vinePayload.orgId = user?.orgId || null;
        vinePayload.contributors = [user?.id || 'unknown_user'];
        vinePayload.contributorCount = 1;
        vinePayload.createdAt = new Date().toISOString();
        vinePayload.notes = []; 

        if (user.roles?.isModerator) {
          // FIX 1: Point to root collection
          await addDoc(collection(db, 'vines_global'), vinePayload);
        } else {
          await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'pending_edits'), {
            ...vinePayload,
            status: 'pending',
            submittedAt: new Date().toISOString()
          });
        }
      }

      resetAndCloseModal();
    } catch (err) {
      console.error(err);
      alert('Submission error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetAndCloseModal = () => {
    setShowAddModal(false);
    setEditingVineId(null);
    setFormData({ producer: '', varietals: '', year: '', country: '', state: '', viticulturalArea: '', vineyard: '', story: '', suggestedPairings: '' });
    setPhotoFile(null);
    setFormError('');
  };

  const handleEditClick = (vine) => {
    setSelectedVine(null); 
    setFormData({
      producer: vine.producer || '',
      varietals: vine.varietals || '',
      year: vine.year || '',
      country: vine.country || '',
      state: vine.state || '',
      viticulturalArea: vine.viticulturalArea || '',
      vineyard: vine.vineyard || '',
      story: vine.story || '',
      suggestedPairings: vine.suggestedPairings || ''
    });
    setEditingVineId(vine.id);
    setShowAddModal(true);
  };

  const handleDeleteClick = async (id, producer) => {
    if (window.confirm(`Are you sure you want to permanently delete ${producer}? This will remove it from the global database for all users.`)) {
      try {
        // FIX 1: Point to root collection
        await deleteDoc(doc(db, 'vines_global', id));
        setSelectedVine(null); 
      } catch (err) {
        alert("Error deleting vine: " + err.message);
      }
    }
  };

  const handleAddCommunityNote = async (e) => {
      e.preventDefault();
      if (!newNoteText.trim() || !selectedVine) return;

      const newNote = {
          id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
          text: newNoteText.trim(),
          authorName: user.name,
          authorId: user.id,
          score: 1, 
          voters: { [user.id]: 1 }, 
          createdAt: new Date().toISOString()
      };

      const currentNotes = selectedVine.notes || [];
      
      try {
          // FIX 1 & 2: Root collection + setDoc with merge: true
          await setDoc(doc(db, 'vines_global', selectedVine.id), {
              notes: [...currentNotes, newNote]
          }, { merge: true });
          setNewNoteText('');
      } catch (err) {
          alert('Failed to post note: ' + err.message);
      }
  };

  const handleVote = async (noteId, voteValue) => {
      if (!selectedVine) return;
      const currentNotes = selectedVine.notes || [];

      const updatedNotes = currentNotes.map(note => {
          if (note.id === noteId) {
              const currentVote = note.voters?.[user.id] || 0;
              
              if (currentVote === voteValue) {
                  const newVoters = { ...note.voters };
                  delete newVoters[user.id];
                  return { ...note, score: note.score - currentVote, voters: newVoters };
              }

              const newScore = note.score - currentVote + voteValue;
              return { ...note, score: newScore, voters: { ...(note.voters || {}), [user.id]: voteValue } };
          }
          return note;
      });

      try {
          // FIX 1 & 2: Root collection + setDoc with merge: true
          await setDoc(doc(db, 'vines_global', selectedVine.id), {
              notes: updatedNotes
          }, { merge: true });
      } catch (err) {
          console.error('Vote failed:', err);
      }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      
      <div className="controls-row">
          <div className="segmented-track">
              <button onClick={() => setFilter('global')} className={`segment-btn ${filter === 'global' ? 'active' : ''}`}><Globe size={16} /> Global</button>
              {user?.orgId && <button onClick={() => setFilter('org')} className={`segment-btn ${filter === 'org' ? 'active' : ''}`}><Users size={16} /> Team</button>}
              <button onClick={() => setFilter('my')} className={`segment-btn ${filter === 'my' ? 'active' : ''}`}><User size={16} /> Mine</button>
          </div>

          <div className="search-add-row">
              <div className="search-container">
                  <Search className="search-icon" size={18} />
                  <input type="text" placeholder="Search producer, varietal, pairings..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
              </div>
              <button onClick={() => { setEditingVineId(null); setShowAddModal(true); }} className="btn-gradient"><Plus size={16} /> Add Vine</button>
          </div>
      </div>

      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('producer')}>Producer {sortConfig.key === 'producer' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
              <th onClick={() => handleSort('year')} style={{textAlign: 'center'}}>Vintage {sortConfig.key === 'year' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
              <th onClick={() => handleSort('varietals')} style={{textAlign: 'center'}}>Varietal {sortConfig.key === 'varietals' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
              <th onClick={() => handleSort('country')} style={{textAlign: 'center'}}>Region {sortConfig.key === 'country' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}</th>
              <th style={{width: '60px'}}></th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredVines.length === 0 ? (
              <tr>
                <td colSpan="5" style={{textAlign: 'center', padding: '2rem', color: '#a0aec0'}}>No vines found. Try adjusting your search or add a new one!</td>
              </tr>
            ) : (
              sortedAndFilteredVines.map((vine) => (
                <tr key={vine.id} onClick={() => setSelectedVine(vine)} className="table-row-hover">
                  <td style={{fontWeight: 700, color: '#2d3748', fontSize: '1rem'}}>{vine.producer}</td>
                  <td style={{textAlign: 'center'}}><span style={{backgroundColor: '#f7fafc', padding: '0.25rem 0.75rem', borderRadius: '0.5rem', fontWeight: 700, color: '#4a5568'}}>{vine.year}</span></td>
                  <td>
                      <div className="flex-center flex-wrap-gap">
                          {vine.varietals && vine.varietals.split(',').map((v, i) => (
                              <span key={i} className="pill-varietal">{v.trim()}</span>
                          ))}
                      </div>
                  </td>
                  <td>
                      <div className="flex-center flex-wrap-gap">
                          {[vine.viticulturalArea, vine.state, vine.country].filter(Boolean).map((loc, i) => (
                              <span key={i} className="pill-region">{loc}</span>
                          ))}
                      </div>
                  </td>
                  <td style={{textAlign: 'right'}}>
                      {user.roles?.isModerator ? (
                          <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem'}}>
                              <button onClick={(e) => { e.stopPropagation(); handleEditClick(vine); }} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: '0.25rem'}} title="Edit Vine"><Edit3 size={16} /></button>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(vine.id, vine.producer); }} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', padding: '0.25rem'}} title="Delete Vine"><Trash2 size={16} /></button>
                          </div>
                      ) : (
                          <ChevronRight className="chevron-icon" size={20} />
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* DETAIL MODAL WITH SCROLL FIX */}
      {selectedVine && (
        <div style={styles.modalOverlay}>
          {/* FIXED: overflowY: 'auto', overflowX: 'hidden' properly restores the vertical scrollbar! */}
          <div style={{...styles.modalContent, padding: 0, overflowY: 'auto', overflowX: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column'}}>
            <button onClick={() => setSelectedVine(null)} style={{position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', padding: '0.5rem', cursor: 'pointer', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                <X size={20} />
            </button>
            
            {selectedVine.photoUrl ? (
                <img src={selectedVine.photoUrl} alt={selectedVine.producer} style={{width: '100%', height: '250px', objectFit: 'cover', borderBottom: '1px solid #e2e8f0'}} />
            ) : (
                <div style={{width: '100%', height: '150px', backgroundColor: '#f7fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e2e8f0'}}>
                    <Wine size={48} color="#cbd5e0" />
                </div>
            )}
            
            <div style={{padding: '2rem', flex: 1}}>
                <div style={{marginBottom: '1.5rem'}}>
                    <h2 style={{fontSize: '2rem', color: theme.primary, marginBottom: '0.25rem'}}>{selectedVine.producer}</h2>
                    <p style={{fontSize: '1.125rem', fontWeight: 600, color: theme.secondary}}>{selectedVine.varietals} • {selectedVine.year}</p>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                    <div style={{backgroundColor: '#f7fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0'}}>
                        <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Terroir & Origin</h4>
                        <div style={{fontSize: '0.875rem', color: '#4a5568', lineHeight: '1.6'}}>
                            {selectedVine.country && <p><span style={{fontWeight: 700, color: '#2d3748'}}>Country:</span> {selectedVine.country}</p>}
                            {selectedVine.state && <p><span style={{fontWeight: 700, color: '#2d3748'}}>State:</span> {selectedVine.state}</p>}
                            {selectedVine.viticulturalArea && <p><span style={{fontWeight: 700, color: '#2d3748'}}>Appellation:</span> {selectedVine.viticulturalArea}</p>}
                            {selectedVine.vineyard && <p><span style={{fontWeight: 700, color: '#2d3748'}}>Vineyard:</span> {selectedVine.vineyard}</p>}
                        </div>
                    </div>
                    
                    {selectedVine.suggestedPairings && (
                        <div style={{backgroundColor: '#f0fdf4', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #bbf7d0'}}>
                            <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#047857', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Suggested Pairings</h4>
                            <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.6'}}>{selectedVine.suggestedPairings}</p>
                        </div>
                    )}
                </div>

                {selectedVine.story && (
                    <div style={{marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e2e8f0'}}>
                        <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Original Curator Note</h4>
                        <p style={{fontSize: '0.875rem', color: '#4a5568', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontStyle: 'italic'}}>{selectedVine.story}</p>
                    </div>
                )}

                <div style={{marginBottom: '1.5rem'}}>
                    <h4 style={{fontSize: '0.875rem', fontWeight: 800, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem'}}>
                        <MessageSquare size={16} color={theme.primary} /> Community Tasting Notes
                    </h4>
                    
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem'}}>
                        {(!selectedVine.notes || selectedVine.notes.length === 0) ? (
                            // FIXED: Plain text styling, no background color, so it doesn't look like a button
                            <p style={{fontSize: '0.875rem', color: '#718096', fontStyle: 'italic', margin: '0 0 1rem 0'}}>No community notes yet. Share your table pitch below!</p>
                        ) : (
                            [...selectedVine.notes].sort((a, b) => b.score - a.score).map((note) => {
                                const myVote = note.voters?.[user.id] || 0;
                                return (
                                    <div key={note.id} style={{display: 'flex', gap: '1rem', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '0.75rem', borderRadius: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.02)'}}>
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '0.25rem'}}>
                                            <button onClick={() => handleVote(note.id, 1)} style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: myVote === 1 ? '#d97706' : '#cbd5e0', transition: 'color 0.2s'}}>
                                                <ChevronUp size={24} strokeWidth={myVote === 1 ? 3 : 2} />
                                            </button>
                                            <span style={{fontSize: '0.875rem', fontWeight: 800, color: myVote !== 0 ? '#d97706' : '#718096'}}>{note.score}</span>
                                            <button onClick={() => handleVote(note.id, -1)} style={{background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: myVote === -1 ? '#718096' : '#cbd5e0', transition: 'color 0.2s'}}>
                                                <ChevronDown size={24} strokeWidth={myVote === -1 ? 3 : 2} />
                                            </button>
                                        </div>
                                        
                                        <div style={{flex: 1}}>
                                            <div style={{display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem'}}>
                                                <span style={{fontSize: '0.75rem', fontWeight: 700, color: '#4a5568'}}>{note.authorName}</span>
                                                <span style={{fontSize: '0.65rem', color: '#a0aec0'}}>{new Date(note.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <p style={{fontSize: '0.875rem', color: '#2d3748', lineHeight: '1.5', whiteSpace: 'pre-wrap'}}>{note.text}</p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <form onSubmit={handleAddCommunityNote} style={{display: 'flex', gap: '0.5rem', alignItems: 'flex-start'}}>
                        <textarea 
                            value={newNoteText} 
                            onChange={(e) => setNewNoteText(e.target.value)} 
                            placeholder="Type your table pitch or tasting note here..." 
                            style={{...styles.textarea, marginBottom: 0, minHeight: '80px', padding: '0.75rem', fontSize: '0.875rem', flex: 1, backgroundColor: '#ffffff'}} 
                            required 
                        />
                        <button type="submit" style={{...styles.btnSecondary, padding: '0.75rem 1.5rem', color: '#ffffff', backgroundColor: theme.primary, borderColor: theme.primary, height: '80px'}}>Post</button>
                    </form>
                </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h2 style={{fontSize: '1.5rem', color: theme.primary, borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.5rem'}}>
                {editingVineId ? 'Edit Vine' : 'Add New Vine'}
            </h2>
            <form onSubmit={handleAddSubmit}>
              
              <div style={{display: 'flex', gap: '1rem'}}>
                <div style={{flex: 2}}>
                  <SelectOrAdd label="Producer" required={true} value={formData.producer} options={uniqueProducers} onChange={(val) => setFormData({ ...formData, producer: val })} placeholder="e.g. Caymus" />
                </div>
                <div style={{flex: 1}}>
                  <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>Vintage *</label>
                  <input type="text" required value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} style={{...styles.input, marginBottom: 0, padding: '0.5rem 0.75rem', fontSize: '0.875rem'}} placeholder="e.g. 2021" />
                </div>
              </div>

              <div style={{marginTop: '1rem'}}>
                <SelectOrAdd label="Varietal(s)" required={true} value={formData.varietals} options={uniqueVarietals} onChange={(val) => setFormData({ ...formData, varietals: val })} placeholder="e.g. Cabernet Sauvignon" />
              </div>

              <div style={{backgroundColor: '#f7fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', marginTop: '1rem', marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.75rem'}}>Regional Data</h4>
                  <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                    <div style={{flex: 1}}>
                      <SelectOrAdd label="Country" required={false} value={formData.country} options={uniqueCountries} onChange={(val) => setFormData({ ...formData, country: val })} placeholder="e.g. USA" />
                    </div>
                    <div style={{flex: 1}}>
                      <SelectOrAdd label="State / Province" required={false} value={formData.state} options={uniqueStates} onChange={(val) => setFormData({ ...formData, state: val })} placeholder="e.g. California" />
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '1rem'}}>
                    <div style={{flex: 1}}>
                      <SelectOrAdd label="Appellation (AVA/AOC)" required={false} value={formData.viticulturalArea} options={uniqueAvas} onChange={(val) => setFormData({ ...formData, viticulturalArea: val })} placeholder="e.g. Napa Valley" />
                    </div>
                    <div style={{flex: 1}}>
                      <SelectOrAdd label="Vineyard" required={false} value={formData.vineyard} options={uniqueVineyards} onChange={(val) => setFormData({ ...formData, vineyard: val })} placeholder="e.g. To Kalon" />
                    </div>
                  </div>
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#047857', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>Suggested Pairings</label>
                <input type="text" value={formData.suggestedPairings} onChange={(e) => setFormData({ ...formData, suggestedPairings: e.target.value })} style={{...styles.input, marginBottom: 0, padding: '0.5rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#f0fdf4', borderColor: '#bbf7d0'}} placeholder="e.g. Wagyu Ribeye, Aged Cheddar..." />
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>Curator Note (Required) *</label>
                <textarea rows="3" required value={formData.story} onChange={(e) => setFormData({ ...formData, story: e.target.value })} style={{...styles.textarea, marginBottom: 0, padding: '0.75rem', fontSize: '0.875rem', borderColor: formError ? theme.error : theme.border}} placeholder="Enter the baseline tasting notes here..." />
                {formError && <p style={{color: theme.error, fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: 600}}>{formError}</p>}
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.25rem', letterSpacing: '0.05em'}}>Bottle Photo {editingVineId && '(Optional)'}</label>
                <input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} style={{fontSize: '0.875rem'}} />
              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem'}}>
                <button type="button" disabled={uploading} onClick={resetAndCloseModal} style={{...styles.btnSecondary, padding: '0.625rem 1.5rem', fontSize: '0.75rem'}}>Cancel</button>
                <button type="submit" disabled={uploading} className="btn-gradient">
                  {uploading ? 'Processing...' : editingVineId ? 'Update Vine' : (user?.roles?.isModerator ? 'Publish Vine' : 'Submit for Review')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// PLAY VIEW
// -----------------------------------------------------------------------------
function PlayView({ db, user }) {
    const [gameState, setGameState] = useState('start');
    const [questions, setQuestions] = useState([]);
    const [currentQ, setCurrentQ] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState({ message: '', type: '', correct: '' });
    const [loading, setLoading] = useState(false);
    const [quizMode, setQuizMode] = useState('global');

    const startQuiz = async () => {
        setLoading(true);
        setFeedback({ message: '', type: '', correct: '' });
        try {
            // FIX 1: Point to root collection
            const winesRef = collection(db, 'vines_global');
            const snap = await getDocs(winesRef);
            const wines = snap.docs.map(d => d.data());

            if (wines.length < 4) {
                setFeedback({ message: 'Not enough wines in the database to generate a quiz (minimum 4 required).', type: 'error' });
                setLoading(false);
                return;
            }

            const generated = [];
            wines.forEach(wine => {
                if (wine.country || wine.viticulturalArea) {
                    const region = wine.viticulturalArea || wine.country;
                    const correct = region;
                    const options = new Set([correct]);
                    const distractors = wines.filter(w => (w.viticulturalArea || w.country) && (w.viticulturalArea || w.country) !== correct).map(w => w.viticulturalArea || w.country);
                    while(options.size < 4 && distractors.length > 0) {
                        options.add(distractors.splice(Math.floor(Math.random() * distractors.length), 1)[0]);
                    }
                    if (options.size === 4) {
                        generated.push({
                            text: `Which region produces ${wine.producer}'s ${wine.year} wine?`,
                            options: Array.from(options).sort(() => Math.random() - 0.5),
                            correct,
                            explanation: `${wine.producer} is located in ${region}.`
                        });
                    }
                }
                if (wine.varietals && wine.varietals.length > 0) {
                    const correct = wine.varietals; 
                    const options = new Set([correct]);
                    const distractors = wines.filter(w => w.varietals && w.varietals !== correct).map(w => w.varietals);
                    while(options.size < 4 && distractors.length > 0) {
                        options.add(distractors.splice(Math.floor(Math.random() * distractors.length), 1)[0]);
                    }
                    if (options.size === 4) {
                        generated.push({
                            text: `What are the primary varietal(s) in ${wine.producer} (${wine.year})?`,
                            options: Array.from(options).sort(() => Math.random() - 0.5),
                            correct,
                            explanation: `The primary varietal(s) for ${wine.producer} are ${wine.varietals}.`
                        });
                    }
                }
            });

            if (generated.length < 5) {
                setFeedback({ message: 'Not enough diverse wine data to generate questions.', type: 'error' });
                setLoading(false);
                return;
            }

            setQuestions(generated.sort(() => Math.random() - 0.5).slice(0, 10));
            setGameState('playing');
            setCurrentQ(0);
            setScore(0);
        } catch (err) {
            setFeedback({ message: 'Error loading quiz: ' + err.message, type: 'error' });
        }
        setLoading(false);
    };

    const handleAnswer = (answer) => {
        const isCorrect = answer === questions[currentQ].correct;
        if (isCorrect) {
            setScore(prev => prev + 10);
            setFeedback({ message: 'Correct!', type: 'success', correct: '' });
        } else {
            setFeedback({ message: 'Not quite!', type: 'error', correct: questions[currentQ].explanation });
        }

        setTimeout(() => {
            if (currentQ < questions.length - 1) {
                setCurrentQ(prev => prev + 1);
                setFeedback({ message: '', type: '', correct: '' });
            } else {
                setGameState('finished');
                saveScore(score + (isCorrect ? 10 : 0));
            }
        }, 2000);
    };

    const saveScore = async (finalScore) => {
        try {
            const scoreRef = doc(db, 'artifacts', appId, 'public', 'data', 'scores', user.id);
            const snap = await getDoc(scoreRef);
            const data = snap.exists() ? snap.data() : { monthlyScore: 0, monthlyMax: 0, lifetimeScore: 0, lifetimeMax: 0, gamesPlayed: 0 };
            
            await setDoc(scoreRef, {
                userId: user.id,
                userName: user.name,
                orgId: user.orgId || 'global',
                monthlyScore: (data.monthlyScore || 0) + finalScore,
                monthlyMax: (data.monthlyMax || 0) + (questions.length * 10),
                lifetimeScore: (data.lifetimeScore || 0) + finalScore,
                lifetimeMax: (data.lifetimeMax || 0) + (questions.length * 10),
                gamesPlayed: (data.gamesPlayed || 0) + 1,
                lastPlayed: serverTimestamp()
            }, { merge: true });
        } catch (err) {
            console.error("Failed to save score", err);
        }
    };

    if (gameState === 'start') {
        return (
            <div style={{textAlign: 'center', padding: '2rem'}}>
                <Gamepad2 size={64} color={theme.primary} style={{margin: '0 auto 1rem auto'}} />
                <h2 style={{fontSize: '2rem', color: theme.primary, marginBottom: '1rem'}}>Quiz Engine</h2>
                <p style={{color: theme.textMuted, maxWidth: '600px', margin: '0 auto 2rem auto'}}>Test your knowledge based on crowdsourced tasting notes and producer details.</p>
                
                {user.orgId && (
                    <div style={{marginBottom: '2rem'}}>
                        <label style={{marginRight: '1rem', fontWeight: 600}}>Select Mode:</label>
                        <select value={quizMode} onChange={(e) => setQuizMode(e.target.value)} style={{...styles.input, width: 'auto', marginBottom: 0}}>
                            <option value="global">Global Database</option>
                            <option value="org">My Organization Only</option>
                        </select>
                    </div>
                )}

                {loading ? (
                    <div style={{color: theme.textMuted}}>Loading questions...</div>
                ) : (
                    <button onClick={startQuiz} style={styles.btnPrimary}>Start Quiz</button>
                )}
                
                {feedback.message && <p style={{color: theme.error, marginTop: '1rem'}}>{feedback.message}</p>}
            </div>
        );
    }

    if (gameState === 'finished') {
        return (
            <div style={{textAlign: 'center', padding: '4rem 2rem'}}>
                <h2 style={{fontSize: '2rem', color: theme.primary, marginBottom: '1rem'}}>Round Complete!</h2>
                <p style={{color: theme.textMuted, marginBottom: '1rem'}}>You scored</p>
                <p style={{fontSize: '4rem', fontWeight: 800, color: theme.secondary, marginBottom: '2rem'}}>{score} / {questions.length * 10}</p>
                <button onClick={() => setGameState('start')} style={styles.btnPrimary}>Play Again</button>
            </div>
        );
    }

    const question = questions[currentQ];
    return (
        <div style={{maxWidth: '800px', margin: '0 auto', textAlign: 'center'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', fontWeight: 600}}>
                <span>Question {currentQ + 1} of {questions.length}</span>
                <span style={{color: theme.primary}}>Score: {score}</span>
            </div>
            
            <div style={{backgroundColor: '#f7fafc', padding: '3rem 2rem', borderRadius: '1rem', marginBottom: '2rem'}}>
                <h3 style={{fontSize: '1.5rem', color: theme.text}}>{question.text}</h3>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                {question.options.map((opt, idx) => (
                    <button 
                        key={idx}
                        onClick={() => handleAnswer(opt)}
                        disabled={!!feedback.message}
                        style={{
                            ...styles.btnSecondary, 
                            padding: '1.5rem', 
                            fontSize: '1.125rem',
                            backgroundColor: feedback.message ? (opt === question.correct ? theme.success : '#edf2f7') : theme.surface,
                            color: feedback.message && opt === question.correct ? 'white' : theme.text,
                            borderColor: feedback.message && opt === question.correct ? theme.success : theme.border
                        }}
                    >
                        {opt}
                    </button>
                ))}
            </div>

            {feedback.message && (
                <div style={{marginTop: '2rem', padding: '1.5rem', borderRadius: '0.5rem', backgroundColor: feedback.type === 'success' ? '#c6f6d5' : '#fed7d7', color: feedback.type === 'success' ? '#22543d' : '#822727'}}>
                    <p style={{fontWeight: 700, fontSize: '1.25rem'}}>{feedback.message}</p>
                    {feedback.correct && <p style={{marginTop: '0.5rem'}}>{feedback.correct}</p>}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// SCORES VIEW
// -----------------------------------------------------------------------------
function ScoresView({ db, user }) {
    const [scores, setScores] = useState([]);
    const [tab, setTab] = useState(user.orgId ? 'team' : 'global');
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'scores'));
        const unsub = onSnapshot(q, (snap) => {
            setScores(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsub();
    }, [db]);

    const displayedScores = scores.filter(s => {
        if (tab === 'team') return s.orgId === user.orgId;
        return true;
    }).sort((a, b) => {
        if (tab === 'lifetime') return (b.lifetimeScore || 0) - (a.lifetimeScore || 0);
        return (b.monthlyScore || 0) - (a.monthlyScore || 0);
    });

    const handleArchiveAndReset = async () => {
        if (!user.orgId) return;
        setLoading(true);
        try {
            const teamScores = scores.filter(s => s.orgId === user.orgId);
            if (teamScores.length > 0) {
                await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'scoreArchives'), {
                    orgId: user.orgId,
                    archivedAt: serverTimestamp(),
                    scores: teamScores
                });
                
                for (const s of teamScores) {
                    const ref = doc(db, 'artifacts', appId, 'public', 'data', 'scores', s.id);
                    await updateDoc(ref, { monthlyScore: 0, monthlyMax: 0 });
                }
            }
            setShowConfirm(false);
        } catch (err) {
            console.error("Archive failed:", err);
        }
        setLoading(false);
    };

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                <h2 style={{fontSize: '1.875rem', color: '#722F37'}}>Leaderboards</h2>
                <div style={{display: 'flex', border: '1px solid #e2e8f0', borderRadius: '0.5rem', overflow: 'hidden'}}>
                    {user.orgId && <button className={`tab-btn ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>Team (Monthly)</button>}
                    <button className={`tab-btn ${tab === 'global' ? 'active' : ''}`} onClick={() => setTab('global')}>Global (Monthly)</button>
                    <button className={`tab-btn ${tab === 'lifetime' ? 'active' : ''}`} onClick={() => setTab('lifetime')}>Lifetime Hall of Fame</button>
                </div>
            </div>

            {tab === 'team' && user.roles?.isFacilitator && (
                <div style={{backgroundColor: '#fff5f5', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fed7d7', marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div>
                        <h4 style={{fontWeight: 700, color: '#9b2c2c', marginBottom: '0.25rem'}}>Facilitator Controls</h4>
                        <p style={{fontSize: '0.875rem', color: '#c53030'}}>Archive this month's results and reset the team board to zero.</p>
                    </div>
                    {showConfirm ? (
                        <div style={{display: 'flex', gap: '0.5rem'}}>
                            <button onClick={handleArchiveAndReset} style={{...styles.btnPrimary, backgroundColor: '#e53e3e'}}>Confirm Reset</button>
                            <button onClick={() => setShowConfirm(false)} style={styles.btnSecondary}>Cancel</button>
                        </div>
                    ) : (
                        <button onClick={() => setShowConfirm(true)} style={{...styles.btnSecondary, color: '#e53e3e', borderColor: '#e53e3e'}}>End Month & Archive</button>
                    )}
                </div>
            )}

            {loading ? (
                <div style={{textAlign: 'center', color: '#718096', padding: '2rem'}}>Loading scores...</div>
            ) : displayedScores.length === 0 ? (
                <div style={{textAlign: 'center', color: '#718096', padding: '4rem', backgroundColor: '#f7fafc', borderRadius: '1rem'}}>
                    No scores recorded for this category yet.
                </div>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                    {displayedScores.map((s, index) => {
                        const scoreVal = tab === 'lifetime' ? (s.lifetimeScore || 0) : (s.monthlyScore || 0);
                        const maxVal = tab === 'lifetime' ? (s.lifetimeMax || 0) : (s.monthlyMax || 0);
                        const proficiency = maxVal > 0 ? Math.round((scoreVal / maxVal) * 100) : 0;

                        return (
                            <div key={s.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', backgroundColor: s.userId === user.id ? '#fffff0' : 'white', border: `1px solid ${s.userId === user.id ? '#D4AF37' : '#e2e8f0'}`, borderRadius: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '1.5rem'}}>
                                    <span style={{fontSize: '1.5rem', fontWeight: 800, color: index < 3 ? '#D4AF37' : '#a0aec0', width: '2rem', textAlign: 'center'}}>
                                        #{index + 1}
                                    </span>
                                    <div>
                                        <h4 style={{fontWeight: 700, fontSize: '1.125rem', color: '#2d3748', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                            {s.userName} {s.userId === user.id && <span style={{fontSize: '0.75rem', backgroundColor: '#D4AF37', color: 'white', padding: '0.1rem 0.5rem', borderRadius: '1rem'}}>You</span>}
                                        </h4>
                                        <p style={{fontSize: '0.875rem', color: '#718096'}}>{s.orgId !== 'global' ? `Team: ${s.orgId}` : 'Global Player'} • {s.gamesPlayed || 0} games</p>
                                    </div>
                                </div>
                                <div style={{textAlign: 'right'}}>
                                    <p style={{fontSize: '1.5rem', fontWeight: 800, color: '#722F37'}}>{scoreVal} <span style={{fontSize: '1rem', color: '#718096', fontWeight: 600}}>pts</span></p>
                                    <p style={{fontSize: '0.875rem', color: proficiency >= 80 ? '#38a169' : '#e53e3e', fontWeight: 600}}>{proficiency}% Accuracy</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// MODERATOR VIEW
// -----------------------------------------------------------------------------
function ModeratorView({ db, user }) {
    const [pendingEdits, setPendingEdits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEdit, setSelectedEdit] = useState(null);
    const [actionStatus, setActionStatus] = useState('');

    useEffect(() => {
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'pending_edits'), where('status', '==', 'pending'));
        const unsub = onSnapshot(q, (snap) => {
            setPendingEdits(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsub();
    }, [db]);

    const handleAction = async (editId, proposedData, action) => {
        setActionStatus('Processing...');
        try {
            if (action === 'approve') {
                const vinePayload = {
                    ...proposedData,
                    createdAt: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                };
                // FIX 1: Point to root collection
                await addDoc(collection(db, 'vines_global'), vinePayload);
            }
            
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'pending_edits', editId), {
                status: action === 'approve' ? 'approved' : 'rejected',
                reviewedBy: user.id,
                reviewedAt: serverTimestamp()
            });
            
            setActionStatus('Success!');
            setTimeout(() => {
                setSelectedEdit(null);
                setActionStatus('');
            }, 1500);
        } catch (error) {
            setActionStatus('Error: ' + error.message);
        }
    };

    return (
        <div>
            <h2 style={{fontSize: '1.875rem', color: '#722F37', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <CheckCircle size={28} /> Moderation Queue
            </h2>
            
            {loading ? (
                <div style={{color: '#718096'}}>Loading queue...</div>
            ) : pendingEdits.length === 0 ? (
                <div style={{textAlign: 'center', padding: '4rem', color: '#718096', backgroundColor: '#f7fafc', borderRadius: '1rem'}}>
                    <Check size={48} style={{margin: '0 auto 1rem auto', opacity: 0.5, color: '#38a169'}} />
                    <h3>Queue is empty!</h3>
                    <p>All pending edits have been reviewed.</p>
                </div>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                    {pendingEdits.map(edit => (
                        <div key={edit.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', border: `1px solid #e2e8f0`, borderRadius: '0.5rem'}}>
                            <div>
                                <h4 style={{fontWeight: 700, fontSize: '1.125rem'}}>{edit.proposedData?.producer || edit.producer}</h4>
                                <p style={{fontSize: '0.875rem', color: '#718096'}}>Submitted by: {edit.creatorName} • {edit.proposedData?.year || edit.year}</p>
                            </div>
                            <button onClick={() => setSelectedEdit(edit)} style={{backgroundColor: '#ffffff', color: '#722F37', border: '1px solid #722F37', padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem'}}><Eye size={18} /> Review</button>
                        </div>
                    ))}
                </div>
            )}

            {selectedEdit && (
                <div style={styles.modalOverlay}>
                    <div style={{...styles.modalContent, padding: '0', position: 'relative'}}>
                        <button onClick={() => setSelectedEdit(null)} style={{position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', padding: '0.5rem', cursor: 'pointer', zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                            <X size={20} />
                        </button>

                        {selectedEdit.photoUrl ? (
                            <img src={selectedEdit.photoUrl} alt="Label" style={{width: '100%', height: '250px', objectFit: 'cover', borderBottom: '1px solid #e2e8f0'}} />
                        ) : (
                            <div style={{width: '100%', height: '150px', backgroundColor: '#f7fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e2e8f0'}}>
                                <Wine size={48} color="#cbd5e0" />
                            </div>
                        )}

                        <div style={{padding: '2rem'}}>
                            <h2 style={{fontSize: '2rem', color: theme.primary, marginBottom: '0.25rem'}}>{selectedEdit.producer || selectedEdit.proposedData?.producer}</h2>
                            <p style={{fontSize: '1.125rem', fontWeight: 600, color: theme.secondary, marginBottom: '1.5rem'}}>
                                {selectedEdit.varietals || selectedEdit.proposedData?.varietals} • {selectedEdit.year || selectedEdit.proposedData?.year}
                            </p>

                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                                <div style={{backgroundColor: '#f7fafc', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0'}}>
                                    <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Regional Data</h4>
                                    <p style={{fontSize: '0.875rem', color: '#4a5568', lineHeight: '1.6'}}>
                                        {selectedEdit.country || selectedEdit.proposedData?.country}<br/>
                                        {selectedEdit.state || selectedEdit.proposedData?.state}<br/>
                                        {selectedEdit.viticulturalArea || selectedEdit.proposedData?.viticulturalArea}<br/>
                                        {selectedEdit.vineyard || selectedEdit.proposedData?.vineyard}
                                    </p>
                                </div>
                                <div style={{backgroundColor: '#f0fdf4', padding: '1rem', borderRadius: '0.75rem', border: '1px solid #bbf7d0'}}>
                                    <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#047857', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Suggested Pairings</h4>
                                    <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.6'}}>
                                        {selectedEdit.suggestedPairings || selectedEdit.proposedData?.suggestedPairings || 'None provided.'}
                                    </p>
                                </div>
                            </div>

                            <div style={{marginBottom: '1.5rem'}}>
                                <h4 style={{fontSize: '0.75rem', fontWeight: 700, color: '#a0aec0', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Tasting Notes & Story</h4>
                                <p style={{fontSize: '0.875rem', color: '#4a5568', lineHeight: '1.6', whiteSpace: 'pre-wrap'}}>
                                    {selectedEdit.story || selectedEdit.proposedData?.story || 'None provided.'}
                                </p>
                            </div>

                            {actionStatus && <p style={{marginBottom: '1rem', fontWeight: 600, color: actionStatus.includes('Error') ? '#e53e3e' : '#38a169'}}>{actionStatus}</p>}

                            <div style={{display: 'flex', gap: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem'}}>
                                <button onClick={() => handleAction(selectedEdit.id, selectedEdit.proposedData || selectedEdit, 'approve')} style={{...styles.btnPrimary, flex: 1, backgroundColor: '#38a169'}}>Approve & Publish</button>
                                <button onClick={() => handleAction(selectedEdit.id, selectedEdit.proposedData || selectedEdit, 'reject')} style={{...styles.btnSecondary, flex: 1, color: '#e53e3e', borderColor: '#e53e3e'}}>Reject</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// FACILITATOR VIEW
// -----------------------------------------------------------------------------
function FacilitatorView({ db, user }) {
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orgName, setOrgName] = useState('');

    useEffect(() => {
        if (!user.orgId) {
            setLoading(false);
            return;
        }

        const fetchOrgName = async () => {
            try {
                const orgRef = doc(db, 'artifacts', appId, 'public', 'data', 'orgs', user.orgId);
                const orgSnap = await getDoc(orgRef);
                if (orgSnap.exists()) {
                    setOrgName(orgSnap.data().name);
                }
            } catch (err) {
                console.error("Error fetching org name:", err);
            }
        };
        fetchOrgName();

        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'users'), where('orgId', '==', user.orgId));
        const unsub = onSnapshot(q, (snap) => {
            setTeamMembers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsub();
    }, [db, user.orgId]);

    const toggleRole = async (memberId, currentRoles, roleToToggle) => {
        const newRoles = { ...currentRoles, [roleToToggle]: !currentRoles[roleToToggle] };
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', memberId), { roles: newRoles });
        } catch (error) {
            console.error("Error updating role:", error);
        }
    };

    const handleRemoveUser = async (memberId, memberName) => {
        if (window.confirm(`Are you sure you want to remove ${memberName} from the organization? They will lose access to team leaderboards and custom vines.`)) {
            try {
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', memberId), { 
                    orgId: null,
                    roles: { isUser: true, isModerator: false, isFacilitator: false, isAdmin: false }
                });
            } catch (error) {
                console.error("Error removing user:", error);
                alert("Failed to remove user: " + error.message);
            }
        }
    };

    if (!user.orgId) {
        return (
            <div style={{textAlign: 'center', padding: '4rem', color: '#718096'}}>
                <Users size={48} style={{margin: '0 auto 1rem auto', opacity: 0.5}} />
                <h3>No Organization Linked</h3>
                <p>You must be part of an organization to access facilitator tools.</p>
            </div>
        );
    }

    return (
        <div>
            <h2 style={{fontSize: '1.875rem', color: '#722F37', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <Users size={28} /> Team Management
            </h2>
            
            <p style={{color: '#718096', marginBottom: '2rem'}}>Manage roles for members of organization <strong>{orgName || user.orgId}</strong>.</p>

            {loading ? (
                <div style={{color: '#718096'}}>Loading team members...</div>
            ) : teamMembers.length === 0 ? (
                <p style={{color: '#718096'}}>No other members found in this organization.</p>
            ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                    {teamMembers.map(member => (
                        <div key={member.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', border: `1px solid #e2e8f0`, borderRadius: '0.5rem', backgroundColor: member.id === user.id ? '#f7fafc' : 'white'}}>
                            <div>
                                <h4 style={{fontWeight: 700, fontSize: '1.125rem', color: '#2d3748'}}>{member.name} {member.id === user.id && <span style={{fontSize: '0.75rem', color: '#718096', fontWeight: 'normal'}}>(You)</span>}</h4>
                                <p style={{fontSize: '0.875rem', color: '#718096'}}>{member.email}</p>
                            </div>
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                                <button 
                                    onClick={() => toggleRole(member.id, member.roles, 'isModerator')} 
                                    style={{padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', backgroundColor: member.roles?.isModerator ? '#ebf8fa' : 'white', border: `1px solid ${member.roles?.isModerator ? '#319795' : '#e2e8f0'}`, color: member.roles?.isModerator ? '#2c7a7b' : '#718096'}}
                                >
                                    Moderator
                                </button>
                                <button 
                                    onClick={() => toggleRole(member.id, member.roles, 'isFacilitator')} 
                                    style={{padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', backgroundColor: member.roles?.isFacilitator ? '#faf5ff' : 'white', border: `1px solid ${member.roles?.isFacilitator ? '#805ad5' : '#e2e8f0'}`, color: member.roles?.isFacilitator ? '#6b46c1' : '#718096'}}
                                >
                                    Facilitator
                                </button>
                                
                                {member.id !== user.id && (
                                    <button 
                                        onClick={() => handleRemoveUser(member.id, member.name)} 
                                        style={{padding: '0.5rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#e53e3e'}}
                                        title="Remove from Team"
                                    >
                                        <UserMinus size={18} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// ADMIN VIEW
// -----------------------------------------------------------------------------
function AdminView({ db }) {
    const [isAuth, setIsAuth] = useState(false);
    const [pin, setPin] = useState('');
    const [orgs, setOrgs] = useState([]);
    const [newOrgName, setNewOrgName] = useState('');
    const [newPasscode, setNewPasscode] = useState('');
    const [editingOrg, setEditingOrg] = useState(null);
    const [editPasscode, setEditPasscode] = useState('');
    
    // Request & User Management States
    const [teamRequests, setTeamRequests] = useState([]);
    const [managingOrg, setManagingOrg] = useState(null);
    const [orgUsers, setOrgUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        if (isAuth) {
            const unsubOrgs = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'orgs'), (snap) => {
                setOrgs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
            
            const unsubReqs = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'team_requests'), where('status', '==', 'pending')), (snap) => {
                setTeamRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });

            return () => { unsubOrgs(); unsubReqs(); };
        }
    }, [isAuth, db]);

    useEffect(() => {
        if (!managingOrg) {
            setOrgUsers([]);
            return;
        }
        setLoadingUsers(true);
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'users'), where('orgId', '==', managingOrg));
        const unsub = onSnapshot(q, (snap) => {
            setOrgUsers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoadingUsers(false);
        });
        return () => unsub();
    }, [managingOrg, db]);

    const handleLogin = (e) => {
        e.preventDefault();
        if (pin === 'vine2026') setIsAuth(true); 
    };

    const handleCreateOrg = async (e) => {
        e.preventDefault();
        if (!newOrgName || !newPasscode) return;
        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'orgs'), {
                name: newOrgName.trim(),
                passcode: newPasscode.trim(),
                isActive: true,
                createdAt: serverTimestamp()
            });
            setNewOrgName('');
            setNewPasscode('');
        } catch (err) { console.error("Error creating org", err); }
    };

    const handleApproveRequest = async (req) => {
        try {
            // 1. Generate an intuitive passcode (e.g. CRED-4921)
            const prefix = req.restaurantName.substring(0, 4).toUpperCase().replace(/[^A-Z]/g, '') || 'TEAM';
            const passcode = `${prefix}-${Math.floor(1000 + Math.random() * 9000)}`;
            
            // 2. Create the Organization
            const orgRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'orgs'), {
                name: req.restaurantName,
                passcode: passcode,
                isActive: true,
                createdAt: serverTimestamp()
            });

            // 3. Update the User (Link to Org & Promote to Moderator/Facilitator)
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', req.requestedBy), {
                orgId: orgRef.id,
                roles: { isUser: true, isModerator: true, isFacilitator: true, isAdmin: false }
            });

            // 4. Mark Request as Approved
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'team_requests', req.id), {
                status: 'approved',
                approvedAt: serverTimestamp(),
                assignedOrgId: orgRef.id
            });

        } catch (err) {
            alert("Approval failed: " + err.message);
        }
    };

    const handleRejectRequest = async (reqId) => {
        if(window.confirm("Are you sure you want to reject this team request?")) {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'team_requests', reqId), {
                status: 'rejected',
                rejectedAt: serverTimestamp()
            });
        }
    };

    const toggleOrgStatus = async (id, currentStatus) => {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'orgs', id), { isActive: !currentStatus });
    };

    const saveNewPasscode = async (id) => {
        if (!editPasscode) return;
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'orgs', id), { passcode: editPasscode.trim() });
        setEditingOrg(null);
        setEditPasscode('');
    };

    const toggleUserRole = async (userId, currentRoles, roleToToggle) => {
        const newRoles = { ...currentRoles, [roleToToggle]: !currentRoles[roleToToggle] };
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userId), { roles: newRoles });
    };

    if (!isAuth) {
        return (
            <div style={{maxWidth: '400px', margin: '4rem auto', textAlign: 'center'}}>
                <ShieldCheck size={48} color="#718096" style={{margin: '0 auto 1rem auto'}} />
                <h2 style={{fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem'}}>Super Admin Access</h2>
                <form onSubmit={handleLogin}>
                    <input type="password" placeholder="Enter Admin PIN" value={pin} onChange={e => setPin(e.target.value)} style={{...styles.input, textAlign: 'center'}} />
                    <button type="submit" style={{...styles.btnPrimary, width: '100%'}}>Unlock Dashboard</button>
                </form>
            </div>
        );
    }

    return (
        <div>
            <h2 style={{fontSize: '1.875rem', color: '#722F37', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <ShieldCheck size={28} /> Vine Master Super Admin
            </h2>

            {teamRequests.length > 0 && (
                <div style={{marginBottom: '2rem'}}>
                    <h3 style={{fontWeight: 700, marginBottom: '1rem', color: '#d97706'}}>Pending Team Requests ({teamRequests.length})</h3>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                        {teamRequests.map(req => (
                            <div key={req.id} style={{backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.5rem', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <div>
                                    <h4 style={{fontWeight: 700, color: '#92400e', fontSize: '1.125rem'}}>{req.restaurantName}</h4>
                                    <p style={{fontSize: '0.875rem', color: '#b45309'}}>Requested by: {req.requesterName} ({req.requesterEmail})</p>
                                    <p style={{fontSize: '0.75rem', color: '#b45309'}}>Expected Users: {req.expectedUsers}</p>
                                </div>
                                <div style={{display: 'flex', gap: '0.5rem'}}>
                                    <button onClick={() => handleApproveRequest(req)} style={{...styles.btnPrimary, backgroundColor: '#38a169', padding: '0.5rem 1rem'}}>Approve & Promote</button>
                                    <button onClick={() => handleRejectRequest(req.id)} style={{...styles.btnSecondary, color: '#e53e3e', borderColor: '#e53e3e', padding: '0.5rem 1rem'}}>Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{backgroundColor: '#f7fafc', padding: '1.5rem', borderRadius: '1rem', border: '1px solid #e2e8f0', marginBottom: '2rem'}}>
                <h3 style={{fontWeight: 700, marginBottom: '1rem', color: '#2d3748'}}>Manually Generate Organization</h3>
                <form onSubmit={handleCreateOrg} style={{display: 'flex', gap: '1rem'}}>
                    <input type="text" placeholder="Organization Name (e.g., The French Laundry)" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} style={{...styles.input, marginBottom: 0, flexGrow: 1}} required />
                    <input type="text" placeholder="Passcode (e.g., NAPA2026)" value={newPasscode} onChange={e => setNewPasscode(e.target.value)} style={{...styles.input, marginBottom: 0, width: '200px'}} required />
                    <button type="submit" style={styles.btnPrimary}><Plus size={18}/> Create</button>
                </form>
            </div>

            <h3 style={{fontWeight: 700, marginBottom: '1rem', color: '#2d3748'}}>Active Licenses ({orgs.length})</h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                {orgs.map(org => (
                    <div key={org.id} style={{backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', overflow: 'hidden'}}>
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem'}}>
                            <div>
                                <h4 style={{fontWeight: 700, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                    {org.name} 
                                    {!org.isActive && <span style={{fontSize: '0.75rem', backgroundColor: '#fed7d7', color: '#c53030', padding: '0.1rem 0.5rem', borderRadius: '1rem'}}>Disabled</span>}
                                </h4>
                                <div style={{fontSize: '0.875rem', color: '#718096', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem'}}>
                                    Passcode: <strong style={{color: '#722F37'}}>{org.passcode}</strong>
                                    <button onClick={() => {setEditingOrg(org.id); setEditPasscode(org.passcode);}} style={{background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', fontSize: '0.75rem', padding: 0}}>Edit</button>
                                </div>
                            </div>

                            <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
                                {editingOrg === org.id && (
                                    <div style={{display: 'flex', gap: '0.5rem'}}>
                                        <input type="text" value={editPasscode} onChange={e => setEditPasscode(e.target.value)} style={{...styles.input, padding: '0.25rem 0.5rem', marginBottom: 0, width: '120px'}} />
                                        <button onClick={() => saveNewPasscode(org.id)} style={{...styles.btnPrimary, padding: '0.25rem 0.75rem'}}>Save</button>
                                        <button onClick={() => setEditingOrg(null)} style={{...styles.btnSecondary, padding: '0.25rem 0.75rem'}}>Cancel</button>
                                    </div>
                                )}
                                <button 
                                    onClick={() => setManagingOrg(managingOrg === org.id ? null : org.id)} 
                                    style={{...styles.btnSecondary, padding: '0.5rem 1rem'}}
                                >
                                    <Users size={16}/> {managingOrg === org.id ? 'Close Users' : 'Manage Users'}
                                </button>
                                <button 
                                    onClick={() => toggleOrgStatus(org.id, org.isActive)} 
                                    style={{...styles.btnSecondary, color: org.isActive ? '#e53e3e' : '#38a169', borderColor: org.isActive ? '#e53e3e' : '#38a169', padding: '0.5rem 1rem'}}
                                >
                                    {org.isActive ? 'Disable Access' : 'Enable Access'}
                                </button>
                            </div>
                        </div>

                        {managingOrg === org.id && (
                            <div style={{backgroundColor: '#f8fafc', padding: '1rem', borderTop: '1px solid #e2e8f0'}}>
                                <h5 style={{fontWeight: 700, color: '#4a5568', marginBottom: '0.75rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Registered Users for {org.name}</h5>
                                
                                {loadingUsers ? (
                                    <p style={{color: '#a0aec0', fontSize: '0.875rem'}}>Loading team members...</p>
                                ) : orgUsers.length === 0 ? (
                                    <p style={{color: '#a0aec0', fontSize: '0.875rem'}}>No users have registered with this passcode yet.</p>
                                ) : (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                                        {orgUsers.map(u => (
                                            <div key={u.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0'}}>
                                                <div>
                                                    <p style={{fontWeight: 600, color: '#2d3748', fontSize: '0.875rem'}}>{u.name}</p>
                                                    <p style={{fontSize: '0.75rem', color: '#a0aec0'}}>{u.email}</p>
                                                </div>
                                                <div style={{display: 'flex', gap: '0.5rem'}}>
                                                    <button 
                                                        onClick={() => toggleUserRole(u.id, u.roles, 'isModerator')} 
                                                        style={{padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.75rem', backgroundColor: u.roles?.isModerator ? '#ebf8fa' : 'white', border: `1px solid ${u.roles?.isModerator ? '#319795' : '#e2e8f0'}`, color: u.roles?.isModerator ? '#2c7a7b' : '#718096'}}
                                                    >
                                                        Moderator
                                                    </button>
                                                    <button 
                                                        onClick={() => toggleUserRole(u.id, u.roles, 'isFacilitator')} 
                                                        style={{padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.75rem', backgroundColor: u.roles?.isFacilitator ? '#faf5ff' : 'white', border: `1px solid ${u.roles?.isFacilitator ? '#805ad5' : '#e2e8f0'}`, color: u.roles?.isFacilitator ? '#6b46c1' : '#718096'}}
                                                    >
                                                        Facilitator
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}